import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, computed } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ListsService } from '../../services/lists.service';
import { CommonModule } from '@angular/common';
import { InputComponent } from "../input/input.component";
import { SelectedComponent } from "../selected/selected.component";
import { AutocompleteComponent } from '../autocomplete/autocomplete.component';
import { AGRICULTURAL_INPUTS, AGRICULTURAL_SELECTORS } from 'app/config/constants.config';
import {
  buildPreloadedConfig,
  buildPreloadedSelectConfig,
  normalizeFieldValue,
  mergeSelectOptionsWithPreload,
  resolveOptions,
  resolveRawFieldValue,
} from '../utils/field-preload.utils';

@Component({
  selector: 'app-agricultural',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    InputComponent,
    SelectedComponent,
    AutocompleteComponent
  ],
  templateUrl: './agricultural.component.html'
})
export class AgriculturalComponent implements OnInit, OnChanges, OnDestroy {
  form: FormGroup;
  dataInputs = AGRICULTURAL_INPUTS;
  dataSelected = AGRICULTURAL_SELECTORS;

  @Output() formValid = new EventEmitter<any>();
  @Input() defaultData: any;
  @Output() validData = new EventEmitter<any>();
  @Input() isCheckData = false;
  @Input() dataLastOnboarding: any;
  @Input() isLightVersion = false;
  @Input() resetTimer = false;

  codpros: any[] = [];
  private isUpdating = false;
  private subscription: any;

  lightDataInputs = computed(() => {
    if (!this.isLightVersion) return this.dataInputs();
    return this.dataInputs().filter((input: any) =>
      input.formName === 'income'
    );
  });

  lightDataSelected = computed(() => {
    if (!this.isLightVersion) return this.dataSelected();
    return [];
  });

  constructor(
    private _listService: ListsService,
    private cdr: ChangeDetectorRef
  ) {
    this.form = new FormGroup({
      indact: new FormControl('', [Validators.required]),
      income: new FormControl('', [Validators.required]),
      academic: new FormControl('', [Validators.required]),
      specialProtecionProgram: new FormControl('', [Validators.required]),
      incomeExtraActivity: new FormControl('', [Validators.required]),
      codpro: new FormControl('', [Validators.required]),
    });
  }

  ngOnInit(): void {
    this.getActivityAgricultural();
    this.getAllAcademicLevels();
    this.getAllSpecialProgram();
    this.getAllProfessions();
    this.updateFormData();

    this.subscription = this.form.valueChanges.subscribe((value) => {
      if (!this.isUpdating) {
        setTimeout(() => {
          this.emitFormData(value);
        }, 50);
      }
    });
  }

  private preloadSnapshot = '';

  private getPreloadKey(data: any): string {
    return JSON.stringify({
      indact: data.indact,
      income: data.income,
      academic: resolveRawFieldValue(data, 'academic'),
      specialProtecionProgram: resolveRawFieldValue(data, 'specialProtecionProgram'),
      incomeExtraActivity: data.incomeExtraActivity,
      codpro: data.codpro,
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['defaultData']?.currentValue) return;

    const newData = changes['defaultData'].currentValue;
    if (!newData || !this.hasAddressData(newData)) return;

    const key = this.getPreloadKey(newData);
    if (key === this.preloadSnapshot) return;

    const isFirstPopulation = this.preloadSnapshot === '';
    this.preloadSnapshot = key;

    if (isFirstPopulation) {
      this.updateFormData();
    }
  }

  private hasAddressData(data: any): boolean {
    return data?.asotip != null && data?.asotip !== '';
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  private emitFormData(value: any) {
    this.formValid.emit({
      ...value,
      _isValid: this.form.valid,
      _formName: 'agricultural'
    });
  }

  private updateFormData(): void {
    this.isUpdating = true;
    const newData = this.defaultData;

    if (newData && Object.keys(newData).length > 0) {
      this.form.patchValue({
        indact: newData.indact ?? '',
        income: normalizeFieldValue(newData.income, 'number'),
        academic: resolveRawFieldValue(newData, 'academic') ?? '',
        specialProtecionProgram: resolveRawFieldValue(newData, 'specialProtecionProgram') ?? '',
        incomeExtraActivity: normalizeFieldValue(newData.incomeExtraActivity, 'number'),
        codpro: newData.codpro ?? '',
      }, { emitEvent: false });

      this.dataInputs.update((inputs: any) =>
        inputs.map((input: any) => buildPreloadedConfig(input, newData[input.formName], input.type))
      );

      this.dataSelected.update((selects: any) =>
        selects.map((select: any) =>
          buildPreloadedSelectConfig(select, newData[select.formName], select.options, newData)
        )
      );
    }

    setTimeout(() => {
      this.isUpdating = false;
    }, 200);
  }

  private refreshSelectConfigs(): void {
    const newData = this.defaultData;
    if (!newData || Object.keys(newData).length === 0) return;

    this.dataSelected.update((selects: any) =>
      selects.map((select: any) => {
        const currentOptions = resolveOptions(select.options);
        const mergedOptions = mergeSelectOptionsWithPreload(currentOptions, newData, select.formName);
        if (mergedOptions.length !== currentOptions.length && select.options?.set) {
          select.options.set(mergedOptions);
        }
        return buildPreloadedSelectConfig(select, newData[select.formName], select.options, newData);
      })
    );
  }

  getAllProfessions() {
    this._listService.getCodpro().subscribe({
      next: (response: any) => { this.codpros = response; },
      error: (error: any) => { console.error('Error cargando profesiones:', error); }
    });
  }

  getActivityAgricultural() {
    this._listService.getSegAgr().subscribe({
      next: (response: any) => {
        this.dataSelected().at(0)?.options.set(response);
        this.refreshSelectConfigs();
      },
      error: (error: any) => { console.error('Error loading Seg Agroicultural:', error); }
    });
  }

  getOptionLabel(fieldName: string, value: any): string {
    if (!value && value !== 0) return 'No especificado';
    const selectConfig = this.dataSelected().find(config => config.formName === fieldName);
    if (selectConfig && selectConfig.options) {
      const option = selectConfig.options().find((opt: any) => opt.value == value);
      return option ? option.label : value.toString();
    }
    return value.toString();
  }

  getProfessionLabel(value: any): string {
    if (!value && value !== 0) return 'No especificado';
    const profession = this.codpros.find(p => p.value == value);
    return profession ? profession.label : value.toString();
  }

  getAllSpecialProgram() {
    this._listService.getAllSpecialProgram().subscribe({
      next: (response: any) => {
        const merged = mergeSelectOptionsWithPreload(
          response,
          this.defaultData,
          'specialProtecionProgram'
        );
        this.dataSelected().at(2)?.options.set(merged);
        this.refreshSelectConfigs();
      },
      error: (error: any) => { console.error('Error loading special programs:', error); }
    });
  }

  getAllAcademicLevels() {
    this._listService.getAllAcademicLevels().subscribe({
      next: (response: any) => {
        this.dataSelected().at(1)?.options.set(response);
        this.refreshSelectConfigs();
      },
      error: (error: any) => { console.error('Error loading academic levels:', error); }
    });
  }

  setFieldEnabled(event: { formName: string; enabled: boolean }): void {
    this.dataInputs.update((inputs: any) =>
      inputs.map((input: any) =>
        input.formName === event.formName ? { ...input, enabled: event.enabled } : input
      )
    );

    this.dataSelected.update((selects: any) =>
      selects.map((select: any) =>
        select.formName === event.formName ? { ...select, enabled: event.enabled } : select
      )
    );
  }

  setCodpro(event: { value: string | number | null } | string | number | null) {
    const codproId = event && typeof event === 'object' && 'value' in event
      ? event.value
      : event;

    this.isUpdating = true;
    this.form.patchValue({ codpro: codproId }, { emitEvent: false });
    this.emitFormData(this.form.value);
    setTimeout(() => {
      this.isUpdating = false;
    }, 100);
  }

  handleValue(event: { formName: string, value: string | number, isValid?: boolean, isDirty?: boolean }) {
    const control = this.form.get(event.formName);
    if (control?.value == event.value) return;

    this.isUpdating = true;
    control?.setValue(event.value, { emitEvent: false });
    this.emitFormData(this.form.value);
    setTimeout(() => {
      this.isUpdating = false;
    }, 100);
  }
}
